import { zodResolver } from "@hookform/resolvers/zod";
import { CircularProgress, Grid, TextField } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
  Controller,
  FormProvider,
  Resolver,
  SubmitHandler,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { z } from "zod";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { CustomFieldValuesSection } from "@/shared/components/CustomFields";
import {
  EventDataEntityType,
  EventDurationForm,
  EventNameInput,
  EventScopeForm,
  EventSource,
  EventSourceType,
  extendCreateEventFormSchema,
} from "@/shared/components/events";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { TargetEntityType } from "@/shared/domain/customFields";
import { EventRecord } from "@/shared/domain/events";
import { useVizState } from "@/shared/state/visualization";
import { nanoSecToSecStr } from "@/shared/time";

import { Unit } from "../../events/schema";
import { MetadataForm, MetadataView } from "../../Metadata";
import { RobotoButton } from "../../RobotoButton";
import { AutocompleteType } from "../../TagAndMetadataAutocomplete";
import { TagInput, Tags, TagsGroup } from "../../tags";
import { autoColorHexForName } from "../colorScale";
import { useEphemeralWorkspaceState } from "../WorkspaceCtx";
import { useCreateWorkspaceEvent } from "../WorkspaceCtx/useWorkspaceEvents";

import styles from "./CreateWorkspaceEventForm.module.css";
import {
  CreateWorkspaceEventFormDefaults,
  makeCreateEventRequest,
  type CreateWorkspaceEventForm as CreateEventForm,
} from "./makeCreateEventRequest";

interface CreateEventFormProps {
  defaultFields?: CreateWorkspaceEventFormDefaults;
  onCancel: () => void;
  onSuccess: (event: EventRecord) => void;
  sourceOptions: Array<EventSource>;
}

const workspaceEventFormSchema = extendCreateEventFormSchema({
  scope: z.object({
    dataEntity: z.object({
      type: z.string(),
    }),
    source: z.object({
      type: z.string(),
      panelId: z.string().optional(),
    }),
  }),
});

function mergeDefaultFormValues(
  defaultFields: CreateWorkspaceEventFormDefaults,
): CreateEventForm {
  const defaults = {
    name: "",
    description: "",
    metadata: {},
    tags: [],
    scope: {
      dataEntity: { type: EventDataEntityType.AllFiles },
      source: { type: EventSourceType.AllPanels },
    },
    unit: Unit.Sec,
  };

  // Convert start_time and end_time bigints to strings because
  // the form requires them as strings.
  const overrideDefaultsWithStartEndTimes = {
    ...defaultFields,
    start_time: nanoSecToSecStr(defaultFields?.start_time ?? 0n),
    end_time: nanoSecToSecStr(defaultFields?.end_time ?? 0n),
  };

  return {
    ...defaults,
    ...overrideDefaultsWithStartEndTimes,
  };
}

const AUTO_COLOR_DEBOUNCE_MS = 1000;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => {
      window.clearTimeout(id);
    };
  }, [value, delayMs]);
  return debounced;
}

// Isolates `name` subscription and auto-color recomputation to the picker row
// so the outer form does not re-render on every keystroke in the name field.
// The auto color is debounced so the picker does not flicker through hues as
// the user types.
const EventColorPicker = () => {
  const { control } = useFormContext<CreateEventForm>();
  const name = useWatch({ control, name: "name" });
  const debouncedName = useDebouncedValue(name, AUTO_COLOR_DEBOUNCE_MS);
  const autoColor = useMemo(
    () => autoColorHexForName(debouncedName),
    [debouncedName],
  );

  return (
    <Controller
      control={control}
      name="color"
      render={({ field }) => (
        <div className={styles.colorRow}>
          <TextField
            variant="outlined"
            margin="dense"
            size="small"
            className={styles.colorInput}
            inputProps={{
              type: "color",
              value: field.value || autoColor,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                field.onChange(e.target.value);
              },
            }}
          />
          {field.value && (
            <button
              type="button"
              onClick={() => field.onChange("")}
              className={styles.clearButton}
            >
              Clear
            </button>
          )}
          <RobotoTypography className={styles.colorLabel}>
            {field.value ? "Custom color" : "Auto-generated"}
          </RobotoTypography>
        </div>
      )}
    />
  );
};

export const CreateWorkspaceEventForm = ({
  defaultFields,
  onSuccess,
  onCancel,
  sourceOptions,
}: CreateEventFormProps) => {
  const workspaceState = useEphemeralWorkspaceState();
  const vizState = useVizState();
  const orgId = useCurrentOrgId();
  const [customFieldValues, setCustomFieldValues] = useState<
    Record<string, unknown>
  >({});
  const form = useForm<CreateEventForm>({
    defaultValues: mergeDefaultFormValues(defaultFields ?? {}),
    resolver: zodResolver(
      workspaceEventFormSchema,
      // TODO workspace schema and CreateEventForm have drifted
    ) as unknown as Resolver<CreateEventForm>,
  });

  const createWorkspaceEvent = useCreateWorkspaceEvent();

  const onSubmit: SubmitHandler<CreateEventForm> = (data) => {
    const request = makeCreateEventRequest(data, vizState, workspaceState);
    const withCustomFields =
      Object.keys(customFieldValues).length > 0
        ? { ...request, custom_fields: customFieldValues }
        : request;
    return createWorkspaceEvent.mutateAsync(withCustomFields).then(onSuccess);
  };

  const submissionErrors = form.formState.errors.root?.serverErrors;

  return (
    <FormProvider {...form}>
      <form
        onSubmit={(event) => {
          form
            .handleSubmit(onSubmit)(event)
            .catch(() => {
              // Display a user friendly error when the request fails.
              form.setError("root.serverErrors", {
                message: "Failed to create event. Please try again.",
              });
            });
        }}
        className={styles.form}
      >
        <Grid container direction="column" justifyContent="center" spacing={2}>
          <Grid item xs={12}>
            <EventScopeForm sourceOptions={sourceOptions} />
          </Grid>
          <Grid item xs={12}>
            <EventDurationForm />
          </Grid>
          <Grid item xs={12}>
            <RobotoTypography component={"h3"} className={styles.sectionTitle}>
              Event name
            </RobotoTypography>
            <Controller
              control={form.control}
              name="name"
              render={({ field }) => {
                return (
                  <EventNameInput
                    currentName={field.value}
                    onNameChanged={field.onChange}
                    error={form.formState.errors.name !== undefined}
                    helperText={form.formState.errors.name?.message}
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <RobotoTypography component={"h3"} className={styles.sectionTitle}>
              Description (optional)
            </RobotoTypography>
            <TextField
              {...form.register("description")}
              placeholder="Description"
              fullWidth
              multiline
              minRows={2}
              maxRows={5}
              error={form.formState.errors.description !== undefined}
              helperText={form.formState.errors.description?.message}
            />
          </Grid>
          <Grid item xs={12}>
            <RobotoTypography component={"h3"} className={styles.sectionTitle}>
              Color (optional)
            </RobotoTypography>
            <EventColorPicker />
          </Grid>
          <Grid item xs={12}>
            <RobotoTypography component={"h3"} className={styles.sectionTitle}>
              Tags (optional)
            </RobotoTypography>
            <Controller
              control={form.control}
              name="tags"
              render={({ field }) => {
                return (
                  <TagsGroup>
                    <Tags
                      tags={field.value}
                      onDeleteTag={(tag) => {
                        field.onChange(field.value.filter((t) => t !== tag));
                      }}
                    />
                    <TagInput
                      tags={field.value}
                      autocompleteType={AutocompleteType.EventTags}
                      onAddTag={(tag) => {
                        field.onChange([...field.value, tag]);
                      }}
                    />
                  </TagsGroup>
                );
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <RobotoTypography
              component={"h3"}
              className={styles.sectionTitleNoBottomMargin}
            >
              Metadata (optional)
            </RobotoTypography>
            <Controller
              control={form.control}
              name="metadata"
              render={({ field }) => {
                return (
                  <>
                    <MetadataView
                      metadata={field.value}
                      onChange={(metadata) => {
                        field.onChange(metadata);
                      }}
                    />
                    <MetadataForm
                      metadata={field.value}
                      autocompleteType={AutocompleteType.EventMetadataKeys}
                      onAddMetadata={(key, value) => {
                        field.onChange({
                          ...field.value,
                          [key]: value,
                        });
                      }}
                    />
                  </>
                );
              }}
            />
          </Grid>
          {/* CustomFieldValuesSection renders null when the flag is off or the
              org has no Ready event fields; `&:empty` then collapses this row so
              it adds no grid spacing. */}
          <Grid item xs={12} sx={{ "&:empty": { display: "none" } }}>
            <CustomFieldValuesSection
              entityType={TargetEntityType.Event}
              orgId={orgId}
              values={customFieldValues}
              onChange={setCustomFieldValues}
              header={
                <RobotoTypography
                  component={"h3"}
                  className={styles.sectionTitle}
                >
                  Custom Fields (optional)
                </RobotoTypography>
              }
            />
          </Grid>
          <Grid item xs={12}>
            <div className={styles.buttonRow}>
              <RobotoButton
                eventName={"WorkspaceEventCreated"}
                eventProperties={{ eventName: form.getValues("name") }}
                type="submit"
                variant="contained"
                color="primary"
                disabled={form.formState.isSubmitting}
                endIcon={
                  form.formState.isSubmitting ? (
                    <CircularProgress size={"1rem"} />
                  ) : null
                }
              >
                Create Event
              </RobotoButton>
              <RobotoButton
                eventName={"CreateWorkspaceEventCanceled"}
                type="button"
                variant="text"
                onClick={onCancel}
              >
                Cancel
              </RobotoButton>
            </div>
            {submissionErrors && (
              <p className={styles.errorMessage}>{submissionErrors.message}</p>
            )}
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};
